import otpInputStyles from '../../utils/otp-input-styles';
import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import OtpInput from 'react-otp-input-rc-17';
import { useHistory, useLocation } from 'react-router';
import type { AlertColor } from '@mui/material/Alert/Alert';
import { API } from 'Amplify';

type Fleet = {
  thingGroupName: string;
  thingGroupId: string;
};

type FleetActionsClaimProps = {
  fleets: Fleet[] | null;
};

function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export function FleetsActionsClaim(props: FleetActionsClaimProps) {
  const shouldForwardToConfigWizard = false;

  const [claim, setClaim] = useState(false);
  const [claimFleet, setClaimFleet] = useState('');
  const [claimCode, setClaimCode] = useState<string | undefined>();
  const [claimError, setClaimError] = useState(null);
  const [claimThing, setClaimThing] = useState(null);
  const [claimReference, setClaimReference] = useState('');
  const [claimType, setClaimType] = useState('claim');
  const [loadClaim, setLoadClaim] = useState(false);
  const query = useQuery();

  const queryClaim = query.get('claim');
  const queryCode = query.get('code');
  const history = useHistory();

  const claimDialogShow = () => {
    setClaim(true);
    setClaimCode('');
    setClaimFleet('');
    setClaimType('claim');
    setClaimError(null);
  };

  const claimDialogClose = () => {
    setClaim(false);
    setClaimCode('');
    setClaimFleet('');
    setClaimError(null);
    history.replace({ search: '' });
  };

  useEffect(() => {
    setClaim(Boolean(queryClaim));

    if (queryCode && queryCode.length > 6) {
      setClaimCode(queryCode.toLowerCase());
      setClaimType('confirmation');
    } else if (queryCode) {
      setClaimCode(queryCode.toUpperCase());
    }
  }, [queryClaim, queryCode]);

  useEffect(() => {
    async function request() {
      if (loadClaim) {
        setClaimError(null);

        try {
          const { thingName } = await API.post('IO_API', `/fleet/device-claim`, {
            body: { code: claimCode, fleetName: claimFleet, reference: claimReference },
          });
          setClaimThing(thingName);
        } catch (e: any) {
          setClaimError(e?.response?.data || e.message || 'Unidentified error');
        }

        setLoadClaim(false);
      }
    }
    request();
  }, [loadClaim]);

  const codeChange = (ev: any) => {
    let newCode = ev.replaceAll(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (claimType === 'confirmation') {
      newCode = newCode.toLowerCase();
    }
    setClaimCode(newCode);
  };

  let alert: string | null = null;
  let alertType: AlertColor = 'info';
  let alertAction = <></>;

  if (claimThing) {
    alertType = 'success';
    alert = `Successfully claimed device '${claimThing}'`;

    if (shouldForwardToConfigWizard) {
      alertAction = (
        <Button
          onClick={() => {
            history.push(`${claimFleet}/devices/${claimThing}/device-details?wizard=true`);
          }}
        >
          Config Wizard
        </Button>
      );
    } else {
      alertAction = (
        <Button
          onClick={() => {
            history.push(`${claimFleet}/devices/${claimThing}/device-details`);
          }}
        >
          Configuration
        </Button>
      );
    }
  }

  if (claimError) {
    alertType = 'error';
    alert = `${claimError}`;
  }

  if (loadClaim) {
    alertType = 'info';
    alert = 'Claiming. . .';
  }

  let noInputs = 6;
  let claimText = 'Claim Code';
  let disableClaim = !(claimCode && claimFleet && claimCode.length >= 6 && !loadClaim);

  if (claimType === 'confirmation') {
    noInputs = 8;
    claimText = 'Confirmation Code';
    disableClaim = !(claimCode && claimFleet && claimCode.length >= 8 && !loadClaim);
  }

  return (
    <>
      <Button
        variant="outlined"
        style={{ backgroundColor: '#ffffff' }}
        onClick={claimDialogShow}
      >
        Claim device
      </Button>

      <Dialog
        open={claim}
        onClose={claimDialogClose}
      >
        <DialogTitle>Device Claim</DialogTitle>
        <DialogContent>
          {alert && (
            <Alert
              severity={alertType}
              action={alertAction}
            >
              {alert}
            </Alert>
          )}

          <FormControl
            fullWidth
            variant="standard"
          >
            <InputLabel>Select fleet</InputLabel>
            <Select
              value={claimFleet}
              onChange={({ target }) => setClaimFleet(target.value)}
            >
              {(props.fleets || []).map(({ thingGroupName, thingGroupId }) => (
                <MenuItem
                  key={thingGroupId}
                  value={thingGroupName}
                >
                  {thingGroupName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          <Typography variant="subtitle2">{claimText}</Typography>
          <OtpInput
            value={claimCode}
            numInputs={noInputs}
            onChange={codeChange}
            containerStyle={otpInputStyles.container}
            inputStyle={otpInputStyles.inputStyle}
          />

          <Divider />

          <TextField
            label="Reference (optional)"
            onChange={(e) => setClaimReference(e.target.value)}
            variant="standard"
          />
        </DialogContent>

        <DialogActions>
          <Button
            disabled={disableClaim}
            onClick={() => setLoadClaim(true)}
          >
            Claim
          </Button>
          <Button onClick={() => setClaim(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
